import React, { useState } from 'react';
import { Combobox, ScrollArea, useCombobox } from '@mantine/core';
import { HudIcon, type IconType } from '../../../Icons/HudIcons';
import classNames from 'classnames';
import { Search } from '../../../Search/Search';
import { StyledComboboxDropdownTargetContainer, StyledDropdown } from './Select.styles';

export type Option = {
  value: string;
  label: string | React.ReactNode;
  selectedLabel?: string | React.ReactNode;
};

export type SelectProps = {
  options: Option[];
  searchable?: boolean;
  selectedOption?: Option;
  prefixIcon?: IconType;
  loading?: boolean;
  warning?: boolean;
  onClose?: () => void;
  onSelect?: (option: Option) => void;
};

const Select: React.FC<SelectProps> = ({
  options,
  searchable,
  onClose,
  selectedOption,
  prefixIcon,
  loading,
  warning,
  onSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const combobox = useCombobox({
    onDropdownClose: () => {
      setSearchTerm('');
      onClose?.();
    },
  });

  const handleOptionSubmit = (value: string) => {
    const option = options.find((option) => option.value === value);
    if (option) {
      onSelect?.(option);
    }
    combobox.closeDropdown();
  };

  return (
    <Combobox store={combobox} onOptionSubmit={handleOptionSubmit} withinPortal={false}>
      <Combobox.DropdownTarget>
        <StyledComboboxDropdownTargetContainer>
          <button
            role="combobox"
            onClick={() => {
              return combobox.toggleDropdown();
            }}
          >
            <div className={classNames('content', { warning })}>
              {prefixIcon && <HudIcon icon={prefixIcon} size="20px" />}
              <span>{selectedOption?.selectedLabel ?? selectedOption?.label}</span>
            </div>
            {loading && (
              <HudIcon className="loadingIndicator" icon="other.spinner" size="20px" />
            )}
            <HudIcon
              icon="designSystem.chevronDown"
              className={classNames('openIndicator', { open: combobox.dropdownOpened })}
              size="14px"
            />
          </button>
        </StyledComboboxDropdownTargetContainer>
      </Combobox.DropdownTarget>
      <StyledDropdown>
        {searchable && (
          <Search
            className="search"
            placeholder="Search..."
            onChange={(event) => setSearchTerm(event.currentTarget.value)}
            value={searchTerm}
          />
        )}
        <Combobox.Options>
          <ScrollArea type="auto" offsetScrollbars scrollbars="y">
            {options
              .filter((option) =>
                option.label?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
              )
              .map((option) => (
                <Combobox.Option key={option.value} value={option.value}>
                  {option.label}
                </Combobox.Option>
              ))}
          </ScrollArea>
        </Combobox.Options>
      </StyledDropdown>
    </Combobox>
  );
};

export default Select;
